import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Control, useController, useWatch } from 'react-hook-form';

import { ChevronDownIcon, InfoCircle } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  FormCombobox,
  FormComboboxOption,
} from '../../../../../../components/combobox/index';
import { Cursor, Tooltip } from '../../../../../../components/tooltip/index';
import { useLocale } from '../../../../../../hooks/index';
import { documentSubCategoryByCategory } from '../../../../constants/document-category.constant';
import { DocumentCategory, DocumentSubCategory } from '../../../../enums/document-category.const';
import { QuickSignFormSchema } from '../../../validation/quick-sign.validation';

export type SubCategoryComboboxProps = {
  control: Control<QuickSignFormSchema>;
  disabled?: boolean;
};

export const SubCategoryCombobox: FC<SubCategoryComboboxProps> = ({
  control,
  disabled = false,
}) => {
  const { messagesLocale } = useLocale();
  const { field, fieldState } = useController({ control, name: 'stepOne.subCategory' });
  const category = useWatch({ control, name: 'stepOne.category' });
  const isOtherCategory = category === DocumentCategory.OTHER;
  const [typedValue, setTypedValue] = useState('');
  const prevCategoryRef = useRef(category);

  const options = useMemo(
    () => (category ? (documentSubCategoryByCategory[category] ?? []) : []),
    [category],
  );
  const selectedOption = options.find((o) => o.value === field.value);
  const displayValue = selectedOption ? messagesLocale[selectedOption.labelKey] : '';
  const normalizedTypedValue = typedValue.trim().toLowerCase();
  const shouldHideErrorWhileTyping = isOtherCategory && typedValue.trim().length > 0;

  const filteredOptions = useMemo(() => {
    if (!isOtherCategory || normalizedTypedValue === '') return options;
    return options.filter(({ labelKey }) =>
      messagesLocale[labelKey].toLowerCase().includes(normalizedTypedValue),
    );
  }, [isOtherCategory, messagesLocale, normalizedTypedValue, options]);

  useEffect(() => {
    if (prevCategoryRef.current !== category) {
      prevCategoryRef.current = category;
      setTypedValue('');
      return;
    }

    if (!isOtherCategory) {
      setTypedValue('');
      return;
    }

    setTypedValue(field.value != null ? String(field.value) : '');
  }, [category, field.value, isOtherCategory]);

  const persistOtherSubCategory = (value: string) => {
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
      field.onChange(undefined);
      return;
    }
    field.onChange(trimmedValue);
  };

  const handleInputChange = (value: string) => {
    setTypedValue(value);
    if (isOtherCategory && value.trim().length === 0) {
      field.onChange(undefined);
    }
  };

  const handleInputBlur = () => {
    if (!isOtherCategory) return;
    persistOtherSubCategory(typedValue);
  };

  const handleSelect = (value: string | undefined) => {
    if (!isOtherCategory) return;
    if (!value) {
      field.onChange(undefined);
      return;
    }
    const selectedOptionByValue = options.find((option) => option.value === value);
    const selectedLocalizedValue = selectedOptionByValue
      ? messagesLocale[selectedOptionByValue.labelKey]
      : value;
    if (value === DocumentSubCategory.OTHER_OTHER) {
      persistOtherSubCategory(selectedLocalizedValue);
      setTypedValue(selectedLocalizedValue);
      return;
    }
    persistOtherSubCategory(value);
    setTypedValue(value);
  };

  return (
    <FormCombobox
      className="relative"
      control={control}
      name="stepOne.subCategory"
      onSelect={handleSelect}
    >
      <div className="relative">
        <ComboboxInput
          {...field}
          disabled={disabled}
          errorMessage={shouldHideErrorWhileTyping ? undefined : fieldState.error?.message}
          icon={<ChevronDownIcon className="mt-3 h-4 w-4 text-gray-500" />}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          placeholder={<AppFormattedMessage id="SUB_CATEGORY" />}
          placeholderTrailing={
            isOtherCategory ? (
              <Tooltip
                className="w-[350px]"
                content={<AppFormattedMessage id="SUB_CATEGORY_OTHER_TOOLTIP" />}
                cursor={Cursor.POINTER}
                placement="top"
              >
                <button
                  aria-label="Info"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  type="button"
                >
                  <InfoCircle className="size-3.5" iconColor="currentColor" />
                </button>
              </Tooltip>
            ) : undefined
          }
          readOnly={!isOtherCategory}
          value={isOtherCategory ? typedValue : displayValue}
          wrapperClassName="min-h-[56px]"
        />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" disabled={disabled} />
      </div>
      <ComboboxOptions className="absolute z-20 w-full rounded border border-gray-200 bg-white p-2 shadow-lg">
        {filteredOptions.length > 0 ? (
          filteredOptions.map(({ value, labelKey }) => (
            <FormComboboxOption
              control={control}
              key={value}
              name="stepOne.subCategory"
              value={value}
            >
              <span className="text-sm font-normal text-gray-700">
                <AppFormattedMessage id={labelKey} />
              </span>
            </FormComboboxOption>
          ))
        ) : (
          <>
            <span className="ml-2 px-2 py-[6px] text-sm font-normal text-gray-700">
              <AppFormattedMessage id="NO_RESULTS_FOUND" />
            </span>
            {isOtherCategory && typedValue.trim().length > 0 && (
              <FormComboboxOption
                className="rounded bg-brand-25 px-2 py-[6px]"
                control={control}
                name="stepOne.subCategory"
                value={typedValue.trim()}
              >
                <span className="truncate text-start text-sm font-[450] text-brand-700">
                  <AppFormattedMessage
                    id="CHOOSE_SUB_CATEGORY_NAME"
                    values={{ subCategoryName: typedValue.trim() }}
                  />
                </span>
              </FormComboboxOption>
            )}
          </>
        )}
      </ComboboxOptions>
    </FormCombobox>
  );
};
